import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import { ISearchResponse } from "../model/SearchResponse";
import { IOrganisation } from "../../organisations/model/Organisation";
import { TSearchQueryRequest } from "../service/SearchService";
import { IOrganisationIndexGroup } from "../../organisations/model/OrganisationIndexGroup";
import OrganisationIndexTreePresentationModel, {
  TOrganisationIndexTreeItem
} from "../../organisations/presenter/model/OrganisationIndexTreePresentationModel";
import { TDocumentType } from "../../documentTypes/model/DocumentType";
import { IUser } from "../../user/model/User";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { IOrganisationIndexCategory } from "../../organisations/model/OrganisationIndexCategory";
import { TSearchCriteriaFilter } from "../model/SearchCriteria";
import { IIndexTypeCategory } from "../../indexTypes/model/IndexTypeCategory";

export interface TSearchCriteriaPresenter extends TLoadingAwarePresenter {
  canCreateDocuments: boolean;
  searchQuery?: TSearchQueryRequest;
  searchResults?: ISearchResponse;
  organisations?: IOrganisation[];
  caseLawDocumentTypes?: TDocumentType[];
  indexCategories?: IOrganisationIndexCategory[];
  sortFields: string[];
  indexTrees?: TOrganisationIndexTreeItem[][];
  courtCaseTypes?: IGenericCodebookItem[];
  decisionTypes?: IGenericCodebookItem[];
  currentLanguage?: string;
  user?: IUser;
  searchText?: string;
}

export interface ISearchCriteriaPresenter
  extends TSearchCriteriaPresenter,
    TPresentable {
  loadReferenceValues(
    indexCategories: IIndexTypeCategory[],
    organisations: IOrganisation[],
    indexTrees: IOrganisationIndexGroup[][],
    currentLanguage: string,
    courtCaseTypes: IGenericCodebookItem[],
    decisionTypes: IGenericCodebookItem[]
  ): void;
  onSearch(): any;
  onUpdateCriteria(query: TSearchCriteriaFilter, reset?: boolean): any;
}

const defaultState: TSearchCriteriaPresenter = {
  sortFields: [],
  canCreateDocuments: false
};

const SearchCriteriaPresenter = withStore<
  ISearchCriteriaPresenter,
  TSearchCriteriaPresenter
>(({ application, store, loader, translate }): ISearchCriteriaPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<
    TSearchCriteriaPresenter & TLoadingAwarePresenter
  >();
  loader.start("search");
  loader.start("criteria");

  const onUpdateCriteria = (query: TSearchCriteriaFilter, reset?: boolean) => {
    const { searchQuery } = _store.getState<TSearchCriteriaPresenter>();
    let _searchQuery = reset
      ? {
          phrase: query.phrase
        }
      : Object.assign({}, searchQuery, query);
    if ((_searchQuery as any).searchText) {
      delete (_searchQuery as any).searchText;
    }
    _store.update({
      searchQuery: _searchQuery,
      searchText: reset ? "" : (query as any).searchText,
      selectedOrganisations: query.organisation_id?.split(",")
    });
  };

  const onSearch = () => {
    const { searchQuery } = _store.getState<TSearchCriteriaPresenter>();

    if (searchQuery) {
      searchQuery.type = "advanced";
      let queryString = Object.keys(searchQuery)
        .map((item) => `${item}=${searchQuery[item] || ""}`)
        .join("&");
      _store.update({
        searchQuery: searchQuery
      });
      return _application.navigator.push({
        pathname: "/search",
        search: `?${queryString}`
      });
    }
  };

  const loadReferenceValues = (
    indexCategories: IIndexTypeCategory[],
    organisations: IOrganisation[],
    organisationIndexTree: IOrganisationIndexGroup[][],
    currentLanguage: string,
    courtCaseTypes: IGenericCodebookItem[],
    decisionTypes: IGenericCodebookItem[]
  ) => {
    const { searchResults } = _store.getState<TSearchCriteriaPresenter>();
    const indexTrees = organisationIndexTree?.map((item) =>
      OrganisationIndexTreePresentationModel(
        item,
        searchResults?.facets?.["index_id_is"]
      )
    );
    _store.update({
      indexCategories,
      organisations,
      indexTrees,
      currentLanguage,
      courtCaseTypes,
      decisionTypes
    });
    const user = _store.getState<any>().user;
    if (user) {
      _store.update({ user });
    }
    onUpdateCriteria({
      organisation_id: organisations.map((item) => item.id.toString()).join(",")
    });
    loader.stop("criteria");
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    onUpdateCriteria,
    onSearch,
    loadReferenceValues
  };
}, defaultState);

export default SearchCriteriaPresenter;
